import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { agendamentoService } from '../../services/agendamentoService';
import { usuarioService, documentoService } from '../../services/entitiesServices';
import { useAuth } from '../../hooks/useAuth';
import { useNotifier } from '../ui/Notifier';
import type { Agendamento, Usuario } from '../../types/entities';

interface Props {
  agendamento?: Agendamento;
  isOpen: boolean;
  isEditing?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  documentoNumero?: string; // Para quando chamado do ApoliceFormModal
  initialDescricaoAgendamento?: string;
  onOpenApolice?: (apolice: any) => void; // Callback para abrir modal de apólice
}

export const AgendamentoFormModal: React.FC<Props> = ({ 
  agendamento, 
  isOpen, 
  isEditing = false, 
  onClose, 
  onSuccess,
  documentoNumero,
  initialDescricaoAgendamento,
  onOpenApolice
}) => {
  useEffect(() => {
    if (isOpen) {
      try { console.debug('AgendamentoFormModal opened', { documentoNumero, initialDescricaoAgendamento }); } catch (e) {}
    }
  }, [isOpen, documentoNumero, initialDescricaoAgendamento]);
  const { user } = useAuth();
  const [usuarioDestinoId, setUsuarioDestinoId] = useState('');
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [horaAgendamento, setHoraAgendamento] = useState('');
  const [descricaoOrigem, setDescricaoOrigem] = useState('');
  const [descricaoAgendamento, setDescricaoAgendamento] = useState('');
  const [status, setStatus] = useState('pendente');
  const [submitting, setSubmitting] = useState(false);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [loadingDocumento, setLoadingDocumento] = useState(false);
  const notifier = useNotifier();

  // Extrair número do documento da descricaoOrigem (formato: "Documento(123456)")
  const extractDocumentoNumero = (descOrigem: string): string | null => {
    const match = descOrigem?.match(/Documento\(([^)]+)\)/);
    return match ? match[1] : null;
  };

  const handleAbrirDocumento = async () => {
    const docNumero = extractDocumentoNumero(descricaoOrigem);
    if (!docNumero || !onOpenApolice) return;
    
    setLoadingDocumento(true);
    try {
      // Buscar documentos recentes (API deve aceitar limit)
      const response = await documentoService.list({ limit: 100 } as any);
      const documentos = response?.data || response || [];
      
      // Filtrar para encontrar o documento exato
      const documento = documentos.find((d: any) => 
        d.numeroDocumento === docNumero || 
        d.numeroApolice === docNumero || 
        d.codigoProposta === docNumero
      );
      
      if (documento) {
        // Buscar documento completo com detalhes
        const docCompleto = await documentoService.getById(documento.id);
        const docData = docCompleto?.data || docCompleto;
        onOpenApolice(docData);
        onClose(); // Fechar modal de agendamento
      } else {
        notifier.show({ message: `Documento ${docNumero} não encontrado`, severity: 'warning' });
      }
    } catch (error) {
      console.error('Erro ao buscar documento:', error);
      notifier.show({ message: 'Erro ao buscar documento', severity: 'error' });
    } finally {
      setLoadingDocumento(false);
    }
  };

  // Carregar usuários da empresa quando modal abrir
  useEffect(() => {
    const loadUsuarios = async () => {
      if (isOpen && user?.empresaId) {
        setLoadingUsuarios(true);
        try {
          const response = await usuarioService.list();
          setUsuarios(response?.data || response || []);
        } catch (error) {
          console.error('Erro ao carregar usuários:', error);
          setUsuarios([]);
        } finally {
          setLoadingUsuarios(false);
        }
      }
    };
    
    loadUsuarios();
  }, [isOpen, user?.empresaId]);

  useEffect(() => {
    if (agendamento && isEditing) {
      setUsuarioDestinoId(agendamento.usuarioDestinoId || '');
      if (agendamento.data) {
        const dt = new Date(agendamento.data);
        setDataAgendamento(dt.toISOString().slice(0, 10));
        setHoraAgendamento(dt.toISOString().slice(11, 16));
      } else {
        setDataAgendamento('');
        setHoraAgendamento('');
      }
      setDescricaoOrigem(agendamento.descricaoOrigem || '');
      setDescricaoAgendamento(agendamento.descricaoAgendamento || '');
      setStatus(agendamento.status || 'pendente');
    } else {
      setUsuarioDestinoId('');
      setDataAgendamento('');
      setHoraAgendamento('');
      // Definir descricaoOrigem baseado no contexto
      if (documentoNumero) {
        setDescricaoOrigem(`Documento(${documentoNumero})`);
      } else {
        setDescricaoOrigem('agendamento');
      }
      // if an initial description is provided (e.g. from Apólice UI), use it
      setDescricaoAgendamento(initialDescricaoAgendamento ?? '');
      setStatus('pendente');
    }
  }, [agendamento, isEditing, isOpen, documentoNumero, initialDescricaoAgendamento]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validação: não permitir datas passadas para novos agendamentos
    if (!isEditing && dataAgendamento) {
      const dataEscolhida = new Date(`${dataAgendamento}T${horaAgendamento || '00:00'}`);
      const agora = new Date();
      agora.setSeconds(0, 0);
      if (dataEscolhida < agora) {
        notifier.show({ message: 'Não é possível agendar para datas passadas', severity: 'warning' });
        return;
      }
    }

    setSubmitting(true);
    try {
      // Combinar data e hora em um único timestamp
      let dataHoraCompleta: string | undefined;
      if (dataAgendamento && horaAgendamento) {
        dataHoraCompleta = new Date(`${dataAgendamento}T${horaAgendamento}`).toISOString();
      } else if (dataAgendamento) {
        dataHoraCompleta = new Date(`${dataAgendamento}T00:00`).toISOString();
      }

      const payload: any = {
        usuarioOrigemId: user?.id || '',
        usuarioDestinoId,
        data: dataHoraCompleta,
        descricaoOrigem,
        descricaoAgendamento,
        status
      };

      if (isEditing && agendamento?.id) {
        await agendamentoService.update(agendamento.id, payload);
      } else {
        await agendamentoService.create(payload);
      }

      notifier.show({ message: isEditing ? 'Agendamento atualizado com sucesso!' : 'Agendamento criado com sucesso!', severity: 'success' });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Erro ao salvar agendamento', err);
      notifier.show({ message: 'Erro ao salvar agendamento', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const modalContent = (
    <div style={{ zIndex: 2147483647 }} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2">
      <div className="bg-white rounded p-4 w-full max-w-md">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium">
            {isEditing ? 'Editar Agendamento' : 'Novo Agendamento'}
          </h3>
          {/* Mostrar botão para abrir documento se descricaoOrigem contém "Documento(...)" */}
          {extractDocumentoNumero(descricaoOrigem) && onOpenApolice && (
            <button
              type="button"
              onClick={handleAbrirDocumento}
              disabled={loadingDocumento}
              className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
              title={`Abrir documento ${extractDocumentoNumero(descricaoOrigem)}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {loadingDocumento ? 'Carregando...' : 'Abrir Apólice'}
            </button>
          )}
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 text-xs">
          <div>
            <label className="block text-gray-700 font-medium mb-1">Usuário de Destino *</label>
            {loadingUsuarios ? (
              <div className="w-full p-2 border rounded bg-gray-50">Carregando usuários...</div>
            ) : (
              <select 
                className="w-full p-2 border rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" 
                value={usuarioDestinoId} 
                onChange={(e) => setUsuarioDestinoId(e.target.value)} 
                required
              >
                <option value="">-- Selecione o usuário --</option>
                {usuarios.map((usuario) => (
                  <option key={usuario.id} value={usuario.id}>
                    {usuario.nome} ({usuario.email})
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-gray-700 font-medium mb-1">Data *</label>
              <input 
                type="date" 
                className="w-full p-2 border rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" 
                value={dataAgendamento} 
                onChange={(e) => setDataAgendamento(e.target.value)} 
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">Hora *</label>
              <input 
                type="time" 
                className="w-full p-2 border rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" 
                value={horaAgendamento} 
                onChange={(e) => setHoraAgendamento(e.target.value)} 
                required
              />
            </div>
          </div>
          
          {/* descricaoOrigem kept in state and sent in payload but not displayed in the UI */}
          
          <div>
            <label className="block text-gray-700 font-medium mb-1">Descrição do Agendamento</label>
            <textarea 
              className="w-full p-2 border rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" 
              value={descricaoAgendamento} 
              onChange={(e) => setDescricaoAgendamento(e.target.value)} 
              rows={8}
              placeholder="Descreva o que precisa ser feito..."
            />
          </div>
          
          <div>
            <label className="block text-gray-700 font-medium mb-1">Status</label>
            <select 
              className="w-full p-2 border rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" 
              value={status} 
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="pendente">Pendente</option>
              <option value="ativo">Ativo</option>
              <option value="concluido">Concluído</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
          <div className="flex justify-end space-x-2">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              disabled={submitting} 
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default AgendamentoFormModal;
